import { XyzTransition } from '@animxyz/react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LoadingScene from '~/components/MFEProvider/lib/ReactSuspense/LoadingScene';
import { useTenant } from '~/components/TenantProvider';
import useFeature from '~/hooks/useFeature';
import useHostingInfo from '~/hooks/useHostingInfo';
import useOnboarding from '~/hooks/useOnboarding';

import AlertBanner from '../components/AlertBanner';
import FirstSiteInstalling from './HostingDetail/components/FirstSiteInstalling';
import HostingLoading from './HostingLoading';

const HostingLegacy = () => {
  const { t } = useTranslation('hosting');
  const { isPending: tenantLoading, isProvisioning: tenantProvisioning } =
    useTenant();

  const {
    hostingAccount,
    isLoading: isLoadingHostingAccount,
    isError,
    errorStatusCode,
    isPending: hostingPending,
    isAwaiting: hostingAwaiting,
    isDeleted,
  } = useHostingInfo();

  const { onboarding, firstSiteId } = useOnboarding();
  const { pathname } = useLocation();
  const [isPlesk] = useFeature('plesk');
  const [isWindows] = useFeature('windows');
  const [sitesEnabled] = useFeature('sites');
  const showOnboardingModal =
    !isPlesk &&
    !isWindows &&
    sitesEnabled &&
    onboarding?.completed === false &&
    firstSiteId;

  const isHostingEmail = pathname.includes('/mail');

  const hostingProductName =
    hostingAccount?.data?.billing?.product_name ?? t('planTitle');

  const isPending = hostingPending || tenantLoading;
  const isAwaiting = hostingAwaiting || tenantProvisioning;

  // Tying the capabilities isPending state to the HostingLoading state
  // so we don't show blank screens on initial load
  // when determining which hosting UI to render (i.e. Cloud/Shared)
  // TODO: combine capabilties into hosting account call
  const [, , isClientReady] = useFeature('sites');

  return (
    <XyzTransition
      // xyz="fade left"
      // appear
      // duration="auto"
      // mode="out-in"
      data-testid="hosting-detail"
    >
      {/* TODO: Create a HostingFork wrapper that determines which Hosting page is loading (i.e. Overview, Emails, etc.) */}
      <Container data-testid="hosting-details-container">
        {(isPending || isError || isDeleted || !hostingAccount || isAwaiting) &&
        !isHostingEmail ? (
          <HostingLoading
            loading={isPending || !isClientReady}
            pending={isAwaiting}
            deleted={isDeleted}
            statusCode={errorStatusCode}
            isError={!!isError}
          />
        ) : (
          <Stack spacing={3}>
            {<AlertBanner />}
            {!isHostingEmail && (
              <Typography variant="h1">
                {isLoadingHostingAccount ? (
                  <Skeleton variant="text" width="30%" />
                ) : (
                  hostingProductName
                )}
              </Typography>
            )}
            {/* TODO: LoadingScene renders in different location if inside a Container or not */}
            <Suspense fallback={<LoadingScene />}>
              <Outlet />
            </Suspense>
            {showOnboardingModal && (
              <FirstSiteInstalling
                siteId={firstSiteId}
                onboarding={onboarding?.completed ?? undefined}
              />
            )}
          </Stack>
        )}
      </Container>
    </XyzTransition>
  );
};

export default HostingLegacy;
